// Admin → Settings → Trust & Verification. Platform master switch for Persona
// identity verification — enforced SERVER-SIDE via platform_settings; this UI
// is only a control surface (confirmation + audit display).
import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { setPageTitle } from "../../lib/format";

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // boolean target state awaiting confirmation

  const load = () => api.adminPlatformSettings().then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { setPageTitle("Admin Settings"); load(); }, []);

  const apply = async () => {
    const target = confirm;
    setConfirm(null);
    setBusy(true);
    try {
      await api.adminPersonaVerificationSet(target);
      toast.success(`Identity verification ${target ? "enabled" : "disabled"} platform-wide`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const on = data ? data.persona_verification_enabled !== false : true; // default ON
  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <p className="section-label">Platform Settings</p>
      <h2 className="type-h3 mt-2">Trust &amp; Verification</h2>

      <div className="card-dark p-6 mt-6" data-testid="persona-toggle-card">
        <div className="flex flex-wrap items-center gap-4">
          <span className="w-11 h-11 rounded-xl bg-[#E50914]/15 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#FF3B45]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display font-bold">Persona Identity Verification</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {on
                ? "Drivers must verify identity before booking; hosts before publishing."
                : "Identity verification is OFF — booking and publishing do not require Persona."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${on ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}
                  data-testid="persona-toggle-status">{data ? (on ? "ON" : "OFF") : "…"}</span>
            <button onClick={() => setConfirm(!on)} disabled={busy || !data}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 ${on ? "bg-[#1F2328] border border-[#27272A] text-zinc-200 hover:border-[#3F3F46]" : "btn-primary"}`}
                    data-testid="persona-toggle-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : on ? "Turn OFF" : "Turn ON"}
            </button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[#27272A] flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-zinc-500">
          <p>Current status: <span className="text-zinc-300 font-semibold" data-testid="persona-toggle-current">{data ? (on ? "Required for booking & publishing" : "Not required") : "Loading…"}</span></p>
          <p>Last changed: <span className="text-zinc-300" data-testid="persona-toggle-updated">{data?.updated_at ? new Date(data.updated_at).toLocaleString() : "Never (default ON)"}</span></p>
          <p>Changed by: <span className="text-zinc-300" data-testid="persona-toggle-by">{data?.updated_by || "—"}</span></p>
        </div>
        <p className="mt-3 text-[11px] text-zinc-600 leading-relaxed">
          Server-enforced via platform_settings.persona_verification_enabled. Verification records are never deleted;
          re-enabling instantly re-gates unverified users. If Persona credentials are missing while ON, protected
          actions stay locked (fail-closed) with &quot;Identity verification is temporarily unavailable&quot;.
        </p>
      </div>

      {confirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="persona-toggle-confirm">
          <div className="card-dark w-full max-w-md p-6">
            <p className="font-display font-bold text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Confirm platform change
            </p>
            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
              {confirm
                ? "Turn ON identity verification? All unverified drivers and hosts will immediately be required to verify with Persona before booking or publishing."
                : "Turn OFF identity verification? Drivers and hosts will immediately be able to book and publish without Persona verification. Existing verification records are kept."}
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setConfirm(null)} className="btn-secondary flex-1 text-sm" data-testid="persona-toggle-cancel">Cancel</button>
              <button onClick={apply} disabled={busy} className="btn-primary flex-1 text-sm" data-testid="persona-toggle-apply">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Yes, turn ${confirm ? "ON" : "OFF"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
